import ExpandableText from '@/components/ExpandableText';
import { PassengersIcon } from '@/components/icons/PassengersIcon';
import InformationTextsModal from '@/components/InformationTextsModal';
import PriceDisplay from '@/components/PriceDisplay';
import PromoCode from '@/components/PromoCode';
import Changeability from '@/components/purchase/card/Changeability';
import Refundability from '@/components/purchase/card/Refundability';
import { TransText } from '@/i18n/trans/text';
import { type AppliedReductionDTO } from '@/types/dto/agent';
import { cn } from '@/utils/cn';
import {
  Button,
  Divider,
  Icons,
  Typography,
} from '@packages/webshop-design-system';
import _kebabCase from 'lodash/kebabCase';
import { useState, type FC, type ReactNode } from 'react';

interface BaseOfferCardProps {
  title: string;
  amount: number;
  isSelected: boolean;
  onSelect: () => void;
  /**
   * Information texts. The first two rows of the first one show on the card;
   * "Read more" opens a modal with all of them stacked (BR-57160).
   */
  descriptions?: Array<string>;
  isRefundable?: boolean;
  isChangeable?: boolean;
  isDisabled?: boolean;
  disabledText?: ReactNode;
  showPassengerInfo?: boolean;
  appliedReduction?: AppliedReductionDTO;
  titleAdornment?: ReactNode;
  ['data-testid']: string;
}

const BaseOfferCard: FC<BaseOfferCardProps> = ({
  title,
  amount,
  isSelected,
  onSelect,
  descriptions = [],
  isRefundable = false,
  isChangeable = false,
  isDisabled = false,
  disabledText,
  showPassengerInfo = false,
  appliedReduction,
  titleAdornment,
  ...props
}) => {
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  return (
    <div
      className={cn(
        'flex w-full flex-col justify-between overflow-hidden rounded-lg border border-primary hover:cursor-pointer hover:bg-primary-pale',
        {
          'border-2 border-primary-dark bg-primary-light': isSelected,
          'cursor-not-allowed border-neutral-light bg-greyscale-100 text-neutral hover:cursor-not-allowed hover:bg-greyscale-100':
            isDisabled,
        }
      )}
      onClick={() => {
        if (!isDisabled) {
          onSelect();
        }
      }}
      data-testid="offer-item"
      aria-disabled={isDisabled || undefined}
    >
      <div
        className={cn(
          'flex flex-grow flex-col gap-1 border-b border-primary px-4 py-3 laptop:gap-0',
          {
            'border-primary-dark': isSelected,
            'border-neutral-light': isDisabled,
          }
        )}
      >
        <div className="flex flex-row items-center justify-between gap-2">
          <div className="flex flex-row items-center gap-1">
            <Typography variant="body1-bold">
              <span>{title}</span>
            </Typography>
            {titleAdornment}
          </div>
          <Typography
            variant="body1-bold"
            className={cn('text-primary', { 'text-neutral': isDisabled })}
          >
            <PriceDisplay amount={amount} />
          </Typography>
        </div>
        {appliedReduction &&
          (appliedReduction.code || appliedReduction.name) && (
            <PromoCode
              className="mt-2"
              code={appliedReduction.code}
              name={appliedReduction.name}
              description={appliedReduction.description}
            />
          )}
        <Divider className="my-3" variant={isSelected ? 'medium' : 'regular'} />
        {/* Read more opens the information modal instead of expanding inline;
            stop propagation so it doesn't also select the offer */}
        <div onClick={(e) => e.stopPropagation()}>
          <ExpandableText
            text={descriptions[0]}
            renderHtml
            onReadMore={() => setIsInfoModalOpen(true)}
          />
        </div>
        <InformationTextsModal
          title={title}
          amount={amount}
          descriptions={descriptions}
          isOpen={isInfoModalOpen}
          onOpenChange={setIsInfoModalOpen}
          data-testid="offer-information-modal"
        />
        <div className="flex flex-col">
          <div className="flex flex-row gap-1">
            <Refundability condition={isRefundable} />
            <Changeability condition={isChangeable} />
          </div>
          {showPassengerInfo && (
            <div className="mt-2 flex flex-row gap-1">
              <PassengersIcon className="text-primary" />
              <Typography variant="body2">
                <TransText i18nKey="priceIsForAllPassengers" />
              </Typography>
            </div>
          )}
          {isDisabled && disabledText && (
            <Typography variant="body2-bold" className="mt-2 text-error-text">
              {disabledText}
            </Typography>
          )}
        </div>
      </div>
      <Button
        variant={isSelected ? 'primary' : 'secondary'}
        onPress={() => {
          if (!isDisabled) {
            onSelect();
          }
        }}
        isDisabled={isDisabled}
        data-testid={_kebabCase(`${props['data-testid']}-${title}-button}`)}
        className={cn('w-full rounded-none border-0')}
        startAdornment={
          isSelected && (
            <Icons.checkCircle width={20} height={20} className="self-center" />
          )
        }
      >
        <Typography variant="body1-bold">
          <TransText i18nKey={isSelected ? 'selected' : 'select'} />
        </Typography>
      </Button>
    </div>
  );
};

export default BaseOfferCard;
